// tabs/AvailabilityTab.tsx
import { useCallback, useEffect, useState } from 'react';
import {
  Button, Group, Divider, SimpleGrid, Card, Text, ActionIcon, Badge,
  Drawer, Stack, Select, TextInput, Switch,
} from '@mantine/core';
import { IconPlus, IconRefresh, IconTrash } from '@tabler/icons-react';
import { useACCounsellingServices, CareAvailabilityView, CareAvailability } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import ConfirmActionModal from './ConfirmActionModal';
import Tooltip from '@/components/ui/overlays/Tooltip';

interface AvailabilityTabProps {
  leaderId: string;
  active: boolean;
  onLeaderChange?: () => void;
}

const DAY_OPTIONS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

export default function AvailabilityTab({ leaderId, active, onLeaderChange }: AvailabilityTabProps) {
  const ACCounsellingServices = useACCounsellingServices();

  const [leaderAvailability, setLeaderAvailability] = useState<CareAvailabilityView[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({
    dayOfWeek: '1' as string | null,
    startTime: '09:00',
    endTime: '17:00',
    supportsVirtual: true,
    supportsInPerson: true,
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('Confirm Remove');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null);

  const fetchLeaderAvailability = useCallback(async () => {
    if (!leaderId) return;
    setLoading(true);
    try {
      const result = await ACCounsellingServices.leader.getAvailability(leaderId);
      setLeaderAvailability(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [leaderId, ACCounsellingServices]);

  useEffect(() => { if (active) fetchLeaderAvailability(); }, [active, fetchLeaderAvailability]);

  const handleAddAvailability = async () => {
    if (!form.dayOfWeek || !leaderId) return;
    setLoading(true);
    try {
      const payload: CareAvailability = {
        availabilityID: 0,
        userId: leaderId,
        dayOfWeek: Number(form.dayOfWeek),
        startTime: form.startTime,
        endTime: form.endTime,
        supportsVirtual: form.supportsVirtual,
        supportsInPerson: form.supportsInPerson,
        isActive: true,
        createdDateTime: 0,
        modifiedDateTime: 0,
      };
      await ACCounsellingServices.leader.addAvailabilitySlot(leaderId, payload as any);
      setForm({ dayOfWeek: '1', startTime: '09:00', endTime: '17:00', supportsVirtual: true, supportsInPerson: true });
      setDrawerOpen(false);
      await fetchLeaderAvailability();
      onLeaderChange?.();
      toast.success('Slot Added', 'Availability slot created successfully.');
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to add availability slot.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAvailability = (availabilityRowId: number, availabilityName?: string) => {
    setConfirmTitle('Remove Availability');
    setConfirmMessage(`Remove this availability${availabilityName ? ` (${availabilityName})` : ''} from the leader?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await ACCounsellingServices.leader.removeAvailabilitySlot(leaderId, availabilityRowId as any);
        await fetchLeaderAvailability();
        onLeaderChange?.();
        toast.warning('Availability Removed', 'Availability removed from leader.');
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove availability.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <ConfirmActionModal
        opened={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      <Drawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} title="Add Availability Slot" position="right" size="md">
        <Stack gap="md">
          <Select
            label="Day of Week"
            placeholder="Select a day"
            data={DAY_OPTIONS}
            value={form.dayOfWeek}
            onChange={(val) => setForm({ ...form, dayOfWeek: val })}
            required
          />
          <TextInput
            label="Start Time"
            type="time"
            value={form.startTime}
            onChange={(e) => setForm({ ...form, startTime: e.currentTarget.value })}
          />
          <TextInput
            label="End Time"
            type="time"
            value={form.endTime}
            onChange={(e) => setForm({ ...form, endTime: e.currentTarget.value })}
          />
          <Switch
            label="Supports Virtual Sessions"
            checked={form.supportsVirtual}
            onChange={(e) => setForm({ ...form, supportsVirtual: e.currentTarget.checked })}
          />
          <Switch
            label="Supports In-Person Sessions"
            checked={form.supportsInPerson}
            onChange={(e) => setForm({ ...form, supportsInPerson: e.currentTarget.checked })}
          />
          <Divider my="sm" />
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button onClick={handleAddAvailability} disabled={!form.dayOfWeek || loading} loading={loading}>
              Add Slot
            </Button>
          </Group>
        </Stack>
      </Drawer>

      <Group gap="sm" justify="right" mb="md">
         <Tooltip label="Refresh">
          <ActionIcon variant="subtle" onClick={fetchLeaderAvailability} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setDrawerOpen(true)}>
          Add Slot
        </Button>
      </Group>
      <Divider mb="md" />
      {leaderAvailability.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No availability slots assigned.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          {leaderAvailability.map((a: any) => (
            <Card key={a.availabilityID} withBorder radius="md" padding="lg">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div>
                  <Text fw={700}>{a.dayName ?? `Day ${a.dayOfWeek}`}</Text>
                  <Text size="sm" c="dimmed">
                    {a.startTime && a.endTime ? `${a.startTime} – ${a.endTime}` : 'All day'}
                  </Text>
                  <Group gap={6} mt="xs">
                    {a.supportsVirtual && <Badge size="sm" variant="light" color="blue">Virtual</Badge>}
                    {a.supportsInPerson && <Badge size="sm" variant="light" color="green">In Person</Badge>}
                  </Group>
                </div>
                <ActionIcon
                  variant="light"
                  color="red"
                  onClick={() => handleRemoveAvailability(a.availabilityID, a.slotDisplay ?? a.dayName)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </>
  );
}